/**
 * Given two Date instances, compare them against each other by **time of day only**. This means
 * that if dateA is 1/2/2020 10:00:00 AM and dateB is 1/1/20 11:00:00 AM, dateA will sort before
 * dateB, because the calendar date is disregarded and only the time of day is taken into account.
 *
 * Returns a standard JavaScript `compareFunction` result so it can be used with Array.sort(), i.e.:
 *      returns -1  if A is before B
 *      returns 1   if A is after B
 *      returns 0   if they are equal
 * @param dateA a Date instance
 * @param dateB a Date instance
 */
export const compareTimeOfDay = (dateA: Date, dateB: Date): number => {
    const formatter = new Intl.DateTimeFormat('en', {
        calendar: 'gregory',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false,
    });

    const now = new Date();

    const formatPartsA = formatter.formatToParts(new Date(dateA));
    const formatPartsB = formatter.formatToParts(new Date(dateB));

    const timePartsA = {
        hours:
            formatPartsA.find(
                ({ type }: DateFormatPart): boolean => type === 'hour',
            )?.value ?? '0',
        minutes:
            formatPartsA.find(
                ({ type }: DateFormatPart): boolean => type === 'minute',
            )?.value ?? '0',
        seconds:
            formatPartsA.find(
                ({ type }: DateFormatPart): boolean => type === 'second',
            )?.value ?? '0',
    };

    const timePartsB = {
        hours:
            formatPartsB.find(
                ({ type }: DateFormatPart): boolean => type === 'hour',
            )?.value ?? '0',
        minutes:
            formatPartsB.find(
                ({ type }: DateFormatPart): boolean => type === 'minute',
            )?.value ?? '0',
        seconds:
            formatPartsB.find(
                ({ type }: DateFormatPart): boolean => type === 'second',
            )?.value ?? '0',
    };

    const startTimeA = new Date(now).setHours(
        parseInt(timePartsA.hours, 10),
        parseInt(timePartsA.minutes, 10),
        parseInt(timePartsA.seconds, 10),
    );

    const startTimeB = new Date(now).setHours(
        parseInt(timePartsB.hours, 10),
        parseInt(timePartsB.minutes, 10),
        parseInt(timePartsB.seconds, 10),
    );

    if (startTimeA < startTimeB) {
        return -1;
    }

    if (startTimeA > startTimeB) {
        return 1;
    }

    return 0;
};
